import React, { useState, useRef, useEffect } from "react";
import classnames from 'classnames';
import {useTenantApi} from '@/hooks/useTenantApi';
import { ByzzerMask } from "../ByzzerMask/ByzzerMask";
import { UserAlertSummary } from "@/types/AlertRun";
import {AgGridReact} from "ag-grid-react";
import {ColDef, IDateFilterParams} from "ag-grid-community";
import { ByzzerMenu } from "../ByzzerMenu";
import { nanoid } from 'nanoid';
import { useBetterNavigate } from "@/utils";
import { confirm } from '@/components/form/ByzzerModal';
import { ByzzerTable } from "@/components/ByzzerTable";
import { useUser } from '@/contexts/UserContext';
import './AlertConfigurationGrid.scss';
import { FLOATING_DATE_PICKER_FILTER_PARAMS } from "@/constants/table.constants";
import { MyAlertsProps } from "@/pages/MyAlertsPage";
import { ByzColDef } from "@/types/TableTypes";
import UseByzzerConditionalLink from "@/hooks/UseByzzerConditionalLink";
import { ByzzerTipIcon } from "@byzzer/ui-components";
import { alertErrorMessageMap, getCodeForOldErrorMessage } from "@/constants/alert.constants";

const baseClassName = 'alert-configuration-grid';
export type AlertConfigurationGridColId = 'id' | 'warnings' | 'alertType' | 'focusBrands' | 'categories' | 'characteristics' | 'focusMarkets' | 'notificationRecipients' | 'updatedDtm' | 'actions' | 'timePeriod';

export const AlertConfigurationGrid = ({
    savedConfiguredAlerts,
    setIsLoading,
    ...props
}: MyAlertsProps) => {
    const { deleteAlert } = useTenantApi();
    const { user, refreshConfiguredAlerts } = useUser();
    const gridRef = useRef<AgGridReact<UserAlertSummary>>(null);
    const menuRefs = useRef<any[]>([]);
    const navigate = useBetterNavigate();

    function handleEdit(userAlert: UserAlertSummary) {
        const searchParam = {
            sku: userAlert.productSku ?? '',
            alertConfigId: userAlert.id.toString() ?? '',
        };
        return UseByzzerConditionalLink({
            path: `/dashboard/configure_alert`,
            search: searchParam,
            state: {},
            content: 'Edit Alert',
        });
    }

    async function handleDelete(userAlert: UserAlertSummary) {
        try {
            if (userAlert?.id) {
                if (
                    await confirm({
                        title: 'Are you sure you wish to delete this Alert?',
                        content: <></>,
                        noLabel: 'Cancel',
                        yesLabel: 'Confirm - Delete',
                    })
                ) {
                    setIsLoading(true)
                    await deleteAlert(userAlert?.id);
                    await refreshConfiguredAlerts();
                    setIsLoading(false)
                }
            } else {
                console.warn(`AlertConfigurationGrid - handleDelete fired - WARNING - no alert Id provided...`);
            }
        } catch (err) {
            // tbd
        }

    };

    const disableMenuItems: boolean = user?.role === 'viewer';

    var filterParams: IDateFilterParams = {
        includeBlanksInEquals: false,
        includeBlanksInGreaterThan: false,
        includeBlanksInLessThan: false,
        includeBlanksInRange: false
    }

    const [columnDefs] = useState<ByzColDef<UserAlertSummary, AlertConfigurationGridColId>[]>([
        {
            colId: 'warnings',
            cellRenderer: ({ data: alert }) => {

                if (alert?.isSkuSubscribed === false) {
                    return (
                        <ByzzerTipIcon
                            children={alertErrorMessageMap['alert_sku_not_subscribed']}
                            type={'warning'}
                        />
                    );
                }

                const shouldDisplayIcon = alert?.lastExecutionError && ['warning', 'error'].includes(alert?.lastExecutionError?.severity);

                if (!shouldDisplayIcon) {
                    return <></>;
                }

                if (alert.lastExecutionError?.severity) {
                    const severity = alert.lastExecutionError.severity;

                    const ogErrorCode = alert?.lastExecutionError?.code?.replace('unknown', '')?.trim() as (keyof typeof alertErrorMessageMap) | '';
                    const errorCodeDerivedFromMessage = getCodeForOldErrorMessage(alert.lastExecutionError?.errorMessage);
                    const resolvedErrorCode = ogErrorCode || errorCodeDerivedFromMessage || 'unknown';

                    const tipMessage = alertErrorMessageMap[resolvedErrorCode];
 
                    const tipType = severity === 'warning' ? 'warning' : severity === 'error' ? 'error' : undefined;

                    return (
                        <ByzzerTipIcon
                            children={tipMessage}
                            type={tipType}
                        />
                    )
                }
            },
            width: 50,
            filter: false,
            floatingFilter: false,
            resizable: false,
            lockPosition: true, // same as 'left'
            sortable: false,
            disableHide: true,
            cellClass: `${baseClassName}__column-no-padding`
        },
        {
            colId: 'alertType',
            headerName: 'Alert Type',
            valueGetter: ({ data: { alertProduct } }) => {
                if (!alertProduct?.title?.length) {
                    return '-'
                }
                return `${alertProduct.title}`
            },
            filterValueGetter: ({ data: { alertProduct } }) => {
                return `${alertProduct?.title}`
            },
        },
        {
            colId: 'id',
            headerName: 'Alert ID',
            hide: true,
            width: 150,
            valueGetter: ({ data: alert }) => {
                return `${alert.id}`;
            },
            tooltipValueGetter: ({ data: alert }) => {
                return `${alert.id}`;
            },
        },
        {
            colId: 'focusBrands',
            headerName: 'Focus Brand(s)',
            // colSpan: ({data}: {data: UserAlertSummary}) => Boolean(data?.refactoredConfig?.productSelections?.length) ? 2 : 1, // should inspect metadata for includeProductSelections ideally, if this is ever to be used
            valueGetter: ({ data: { refactoredConfig } }) => {
                if (refactoredConfig?.productSelections?.length) {
                    return refactoredConfig?.productSelections.flatMap((ps, i) => ps?.focusBrands)
                }
                return refactoredConfig?.focusBrands;
            },
            cellRenderer: ({ value, data: { refactoredConfig } }) => {
                const hasProductSelections = Boolean(refactoredConfig?.productSelections?.length);
                const hasFocusBrands = Boolean(refactoredConfig?.focusBrands?.length);

                if (!hasProductSelections && !refactoredConfig?.focusBrands?.length) {
                    return '-'
                }

                return (
                    <div className={classnames({
                            [`${baseClassName}__focusBrands`]: hasFocusBrands,
                            [`${baseClassName}__productSelections-focusBrands`]: hasProductSelections,
                    })}>
                        {value?.map((val, i) => <p key={nanoid()}>{val}{i !== value.length-1 && hasFocusBrands ? '; ' : ''}</p>)}
                    </div>
                )
            },
            wrapText: true,
            width: 450    
        },
        {
            colId: 'categories',
            headerName: 'Category(s)',
            valueGetter: ({ data: { refactoredConfig } }) => {
                if (refactoredConfig?.productSelections?.length) {
                    return refactoredConfig?.productSelections.flatMap((ps, i) => ps?.categories)
                }
                return refactoredConfig?.categories;
            },
            cellRenderer: ({ value, data: { refactoredConfig } }) => {
              
                const hasProductSelections = Boolean(refactoredConfig?.productSelections?.length);
                const hasCategories = Boolean(refactoredConfig?.categories?.length);

                if (!hasProductSelections && !hasCategories) {
                    return '-'
                }

                return (
                    <div className={classnames({
                            [`${baseClassName}__categories`]: hasCategories,
                            [`${baseClassName}__productSelections-categories`]: hasProductSelections,
                    })}>
                        {value?.map((val, i) => <p key={nanoid()}>{val}{i !== value.length-1 && hasCategories ? '; ' : ''}</p>)}
                    </div>
                )
            },
            width: 375,
            wrapText: true,
            autoHeight: true
        },
        {
            colId: 'characteristics',
            headerName: 'Characteristic(s)',
            valueGetter: ({ data: { refactoredConfig } }) => {
                const chars = refactoredConfig?.characteristics?.filter((c)=>Boolean(c.characteristicDisplayValue));
                if (!chars?.length) {
                    return '-'
                }
                return chars.map((c, i) => {
                    return (
                        <React.Fragment key={nanoid()}>
                            {`${c.characteristicDisplayValue} ${c.condition} ${c.value}`}
                        </React.Fragment>
                    )
                })
            },
            filterValueGetter: ({ data: { refactoredConfig } }) => {
                return `${refactoredConfig?.characteristics.map((c) => {
                    return (
                            `${c.characteristicDisplayValue} ${c.condition} ${c.value}`
                    )
                })}`
            },
            cellRenderer: ({ data: { refactoredConfig } }) => {
                const chars = refactoredConfig?.characteristics?.filter((c)=>Boolean(c.characteristicDisplayValue));
                if (!chars?.length) {
                    return '-'
                }
                return chars.map((c, i) => {
                    return (
                        <div key={nanoid()} className={classnames(`${baseClassName}__characteristics`)} >
                            <p>{`${c.characteristicDisplayValue} ${c.condition} ${c.value.join(", ")}${Boolean(i !== chars.length-1) ? '; ' : ''}`}</p>
                        </div>
                    )
                })
            },
            width: 600,
            wrapText: true,
            autoHeight: true
        },
        {
            colId: 'focusMarkets',
            headerName: 'Focus Market',
            valueGetter: ({ data: { refactoredConfig } }) => {
                if (!refactoredConfig?.markets?.length) {
                    return '-'
                }
                return refactoredConfig?.markets?.map((market) => `${market?.name} ${market?.remainingMarketRunConfig?.name ? `vs ${market?.remainingMarketRunConfig?.name}` : ''}`)
            },
            cellRenderer: ({ data: { refactoredConfig } }) => {
                return (
                    <div className={classnames(`${baseClassName}__focusMarkets`)} >
                        {refactoredConfig?.markets?.map((market, i) => <span key={nanoid()}>{market.name}{market?.remainingMarketRunConfig?.name ? ` vs ${market?.remainingMarketRunConfig?.name}` : ''}{i !== refactoredConfig?.markets.length-1 ? '; ' : ''}</span>)}
                    </div>
                );
            },
            width: 500,
            wrapText: true,
            autoHeight: true
        },
        {
            colId: 'timePeriod',
            headerName: 'Time Period',
            valueGetter: ({ data: {refactoredConfig}}: {data: UserAlertSummary }) => refactoredConfig?.timePeriod?.period
        },
        {
            colId: 'notificationRecipients',
            headerName: 'Notification Recipient(s)',
            valueGetter: ({ data: { refactoredConfig } }) => {
                if (!refactoredConfig?.notification?.recipientEmails?.length) {
                    return '-'
                }
                return refactoredConfig?.notification?.recipientEmails?.map((notif: any) => notif?.email ?? notif)
            },
            filterValueGetter: ({ data: { refactoredConfig } }) => {
                return `${refactoredConfig?.notification?.recipientEmails?.map((notif: any) => notif?.email ?? notif)}`
            },
            cellRenderer: ({ value }) => {
                return (
                    Array.isArray(value) ? (
                        <div className={classnames(`${baseClassName}__notifications`)} >
                            {value?.map((val, i) => <span key={nanoid()}>{val}{i !== value.length-1 ? '; ' : ''}</span>)}
                    </div>
                    ) : value
                );
            },
            // https://www.ag-grid.com/react-data-grid/cell-rendering/
            width: 550,
            wrapText: true,
            autoHeight: true
        },
        {
            ...FLOATING_DATE_PICKER_FILTER_PARAMS,
            headerName: 'Updated Date',
            colId: 'updatedDtm',
            field:'updatedDtm',
            tooltipValueGetter: ({ data }) => {
                return `${data?.updatedDtm}`;
            },
            valueFormatter: ({ data }) => {
                return `${data?.updatedDtm ?? 'NA'}`;
            },
            width: 200,
        },
        {
            colId: 'actions',
            cellRenderer: ({ data }) => {
                const { id } = data;
                return (<>
                        <div
                            key={id}
                            className={classnames(`${baseClassName}__menu-trigger`)}
                        ref={thisElement => menuRefs.current[String(id)] = {current: thisElement}}
                        />
                        <ByzzerMenu
                            className={`${baseClassName}__menu`}
                            reference={menuRefs.current[String(id)]}
                            offset={[-110, -30]}
                            triggerTarget={menuRefs.current[String(id)]?.current}
                            items={[
                                {
                                    content: handleEdit(data),
                                    disabled: disableMenuItems,
                                },
                                {
                                onClick: function() {
                                    handleDelete(data)
                                    },
                                    content: 'Delete Alert',
                                disabled: disableMenuItems
                            }
                            ]}
                        />
                </>)
            },
            pinned: "right", 
            width: 65,
            filter: false,
            floatingFilter: false,
            resizable: false,
            lockPosition: 'right',
            sortable: false
        }
    ]);

    return (
        <ByzzerTable
            ref={gridRef} // Ref for accessing Grid's API
            rowData={savedConfiguredAlerts} // Row Data for Rows
            columnDefs={columnDefs} // Column Defs for Columns
            baseColumnWidth={360}
            tableArea={'configuredAlerts'}
            enableColumnHideShow={true}
            enableSaveLayout={true}
            enableSaveSort={true}
            uncheckedColumns={['id']}
        />
    );

};

export default AlertConfigurationGrid;

AlertConfigurationGrid.displayName = 'AlertConfigurationGrid';